<template>
  <div class="sd-tool">
    <!-- Mode selector -->
    <div class="sd-mode-bar">
      <el-radio-group v-model="mode" size="mini" @change="onModeChange">
        <el-radio-button label="direct">{{ $t('tool.SwaggerDoc.directMode') }}</el-radio-button>
        <el-radio-button label="agent">{{ $t('tool.SwaggerDoc.agentMode') }}</el-radio-button>
      </el-radio-group>
      <a v-if="mode==='agent'" :href="agentDownloadUrl" target="_blank" class="sd-agent-dl">
        <i class="el-icon-download" /> {{ $t('tool.SwaggerDoc.downloadAgent') }}
      </a>
    </div>

    <!-- Agent help -->
    <div class="sd-agent-help" v-if="mode==='agent'">
      <i class="el-icon-info" /> {{ $t('tool.SwaggerDoc.agentHelp') }}
    </div>

    <el-form ref="form" :model="form" label-width="110px" size="small">
      <!-- Agent mode: sessionId -->
      <el-form-item v-if="mode==='agent'" :label="$t('tool.SwaggerDoc.sessionId')" :rules="[{required:true,message:$t('tool.SwaggerDoc.sessionIdRequired')}]" prop="sessionId">
        <el-input v-model="form.sessionId" :placeholder="$t('tool.SwaggerDoc.phSessionId')" />
      </el-form-item>
      <el-form-item :label="$t('tool.SwaggerDoc.version')" :rules="[{required:true}]" prop="version">
        <el-select v-model="form.version" style="width:100%;">
          <el-option :label="$t('tool.SwaggerDoc.version2')" value="v2" />
          <el-option :label="$t('tool.SwaggerDoc.version3')" value="v3" />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('tool.SwaggerDoc.url')" :rules="[{required:true,message:$t('tool.SwaggerDoc.urlRequired')}]" prop="url">
        <el-input v-model="form.url" placeholder="http://localhost:8080/v2/api-docs?group=WEB" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="loading" @click="doExport">{{ $t('tool.SwaggerDoc.btnGenerate') }}</el-button>
        <el-button @click="doClear">{{ $t("common.action.clear") }}</el-button>
      </el-form-item>
    </el-form>

    <!-- Error -->
    <div class="sd-card-err" v-if="error">
      <div class="sd-err">{{ error }}</div>
    </div>

    <div class="sd-tip">
      <p><i class="el-icon-info" /> {{ $t('tool.SwaggerDoc.tipUsage') }}</p>
      <p><i class="el-icon-info" /> {{ $t('tool.SwaggerDoc.tipExample2') }}：<code>http://host:port/v2/api-docs?group=WEB</code></p>
      <p><i class="el-icon-info" /> {{ $t('tool.SwaggerDoc.tipExample3') }}：<code>http://host:port/v3/api-docs</code></p>
    </div>
  </div>
</template>

<script>
import request from '@/api/request'

export default {
  name: 'SwaggerDocTool',
  props: { config: { type: Object, default: () => ({}) } },
  data() {
    return {
      mode: 'direct',
      form: { version: 'v2', url: '', sessionId: '' },
      loading: false,
      error: '',
      ws: null
    }
  },
  computed: {
    agentDownloadUrl() {
      return 'https://onltool.site/static/agent.exe'
    }
  },
  beforeDestroy() {
    this.closeWs()
  },
  methods: {
    closeWs() {
      const old = this.ws
      if (old) { old.onclose = null; old.close() }
      this.ws = null
    },
    onModeChange() {
      this.closeWs(); this.error = ''
    },
    doExport() {
      this.error = ''
      if (this.mode === 'agent') {
        if (!this.form.sessionId) {
          this.$message.warning(this.$t('tool.SwaggerDoc.sessionIdRequired'))
          return
        }
        this.exportViaAgent()
      } else {
        this.$refs.form.validate(valid => {
          if (!valid) return
          this.exportDirect()
        })
      }
    },
    exportDirect() {
      this.loading = true
      request({
        url: '/api/pub/swagger-doc/export',
        method: 'post',
        data: { version: this.form.version, url: this.form.url },
        responseType: 'blob'
      }).then(res => {
        this.downloadBlob(res)
      }).catch(() => {
        this.$message.error(this.$t('tool.SwaggerDoc.generateFail'))
      }).finally(() => { this.loading = false })
    },
    exportViaAgent() {
      this.closeWs()
      this.loading = true
      const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsUrl = protocol + '//' + location.host + '/ws/browser/' + this.form.sessionId

      const connectTimeout = setTimeout(() => {
        this.error = this.$t('tool.SwaggerDoc.errAgentConnect')
        this.loading = false
        this.closeWs()
      }, 10000)

      let responseTimeout = null

      this.ws = new WebSocket(wsUrl)
      this.ws.onopen = () => {
        clearTimeout(connectTimeout)
        this.ws.send(JSON.stringify({
          type: 'swaggerDoc',
          url: this.form.url,
          version: this.form.version
        }))
        responseTimeout = setTimeout(() => {
          this.error = this.$t('tool.SwaggerDoc.errAgentConnect')
          this.loading = false
          this.closeWs()
        }, 30000)
      }
      this.ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data)
          clearTimeout(responseTimeout)
          this.handleAgentMsg(msg)
        } catch (ex) {}
      }
      this.ws.onerror = () => {
        clearTimeout(connectTimeout); clearTimeout(responseTimeout)
        this.error = this.$t('tool.SwaggerDoc.errAgentConnect')
        this.loading = false
      }
      this.ws.onclose = () => {
        clearTimeout(responseTimeout)
        if (this.loading) {
          this.error = this.$t('tool.SwaggerDoc.errAgentConnect')
          this.loading = false
        }
        this.ws = null
      }
    },
    handleAgentMsg(msg) {
      switch (msg.type) {
        case 'swaggerDocResult':
          // Send fetched JSON to backend for Word generation
          request({
            url: '/api/pub/swagger-doc/export',
            method: 'post',
            data: { version: msg.version, json: msg.json },
            responseType: 'blob'
          }).then(res => {
            this.downloadBlob(res)
          }).catch(() => {
            this.$message.error(this.$t('tool.SwaggerDoc.generateFail'))
          }).finally(() => { this.loading = false })
          break
        case 'error':
          this.error = msg.message || 'Unknown error'
          this.loading = false
          break
        case 'agentDisconnected':
          this.error = this.$t('tool.SwaggerDoc.agentDisconnected')
          this.loading = false
          break
        default: break
      }
    },
    downloadBlob(res) {
      const url = window.URL.createObjectURL(new Blob([res]))
      const a = document.createElement('a')
      a.href = url
      a.download = 'API_Document.doc'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      this.$message.success(this.$t('tool.SwaggerDoc.generateSuccess'))
    },
    doClear() {
      this.form = { version: 'v2', url: '', sessionId: this.form.sessionId }
      this.error = ''
    },
    doAction(a) { if (a === 'clear') this.doClear() }
  }
}
</script>

<style scoped>
.sd-tool { width: 100%; }

/* Mode bar */
.sd-mode-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.sd-agent-dl { font-size: 12px; color: #4e7cff; text-decoration: none; font-weight: 600; margin-left: auto; }
.sd-agent-dl:hover { text-decoration: underline; }
.sd-agent-dl i { margin-right: 3px; }
.sd-agent-help { font-size: 12px; color: #909399; background: #f0f9ff; border: 1px solid #d0e8ff; border-radius: 8px; padding: 8px 12px; margin-bottom: 14px; display: flex; align-items: center; gap: 6px; }
.sd-agent-help i { color: #4e7cff; flex-shrink: 0; }

/* Error */
.sd-card-err { background: #fff5f5; border: 1px solid #fde0e0; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; }
.sd-err { font-size: 13px; color: #f56c6c; }

.sd-tip {
  margin-top: 24px; padding: 14px 16px;
  background: #f8f9fb; border-radius: 8px; border: 1px solid #eef0f4;
}
.sd-tip p { margin: 0 0 6px; font-size: 12px; color: #8892a6; display: flex; align-items: flex-start; gap: 6px; }
.sd-tip i { color: #4e7cff; margin-top: 1px; }
.sd-tip code { font-size: 11px; background: #eef0f4; padding: 1px 4px; border-radius: 3px; }
</style>
